/**
 * Semantic summary view — renders a `TSummaryRenderPlan` with progressive
 * disclosure: hero (who/what/why) → KPI grid → AI insights → recommendations
 * → primary sections → collapsible "More details" for the long tail.
 *
 * This component knows nothing about the payload schema; everything it shows
 * comes from the semantic engine's plan.
 */

import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  ChevronDown,
  ChevronUp,
  CircleDollarSign,
  FileText,
  Lightbulb,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";

import { AiAssistedDisclaimer } from "@/components/shared";
import { AppCard } from "@/components/ui/AppCard";
import type { TRenderSection, TSummaryInsight } from "@/lib/semantic-summary";
import { buildSummaryRenderPlan } from "@/lib/semantic-summary";
import { cn } from "@/lib/utils";

import { BLOCK_RENDERERS } from "./renderers";

const HERO_CLAMP_THRESHOLD = 420;

type TSemanticSummaryViewProps = {
  /** Raw summary payload from the API (string or object). */
  raw: unknown;
  companyName?: string | null;
  companyRole?: string | null;
  /** Opens a chat thread seeded with the recommendation text. */
  onDeepDive?: (recommendation: string, index: number) => void;
  deepDivingIndex?: number | null;
};

function InsightCard({ insight }: { insight: TSummaryInsight }) {
  const toneStyles = {
    positive: "border-green-600/20 bg-green-600/5 text-green-700 dark:text-green-500",
    neutral: "border-primary/20 bg-primary/5 text-primary",
    caution: "border-amber-500/25 bg-amber-500/5 text-amber-700 dark:text-amber-500",
  }[insight.tone];

  const Icon = insight.tone === "caution" ? AlertTriangle : TrendingUp;

  return (
    <div className={cn("flex items-start gap-2 rounded-xl border px-3 py-2.5", toneStyles)}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <p className="text-xs font-medium leading-relaxed">{insight.text}</p>
    </div>
  );
}

/** Pick a metric icon from label keywords (money, raise, people, default). */
function metricIcon(label: string) {
  const lowered = label.toLowerCase();
  if (/(raise|target|round|funding|seed)/.test(lowered)) return Target;
  if (/(mrr|arr|revenue|burn|cash|runway|margin|cac|ltv|price|cost)/.test(lowered)) return CircleDollarSign;
  if (/(team|employee|user|customer|founder|client)/.test(lowered)) return Users;
  return BarChart3;
}

/** FUNDRAISER / INVESTOR → human chip label. */
function roleLabel(role: string | null | undefined): string | null {
  if (!role) return null;
  const lowered = role.toLowerCase();
  if (lowered.includes("fundraiser") || lowered.includes("founder")) return "Founder";
  if (lowered.includes("investor")) return "Investor";
  return null;
}

/** Pick a section icon from title keywords, matching the metric-icon language. */
function sectionIcon(title: string) {
  const lowered = title.toLowerCase();
  if (/(focus|criteria|preference|mandate)/.test(lowered)) return Target;
  if (/(thesis|strategy|vision)/.test(lowered)) return Lightbulb;
  if (/(portfolio|company|companies|startup)/.test(lowered)) return Briefcase;
  if (/(track record|exit|history|performance|highlight)/.test(lowered)) return Trophy;
  if (/(traction|growth|metric|revenue)/.test(lowered)) return TrendingUp;
  if (/(team|founder|people)/.test(lowered)) return Users;
  return FileText;
}

/** Shared header treatment for every content group on the page. */
function SectionHeader({ title, icon: Icon }: { title: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </span>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
    </div>
  );
}

function SectionCard({ section }: { section: TRenderSection }) {
  return (
    <AppCard
      padding="lg"
      className="space-y-5"
    >
      <div className="border-b border-border pb-3.5">
        <SectionHeader
          title={section.title}
          icon={sectionIcon(section.title)}
        />
      </div>
      {section.blocks.map((block) => {
        const Renderer = BLOCK_RENDERERS[block.kind];
        return (
          <Renderer
            key={block.id}
            node={block.node}
          />
        );
      })}
    </AppCard>
  );
}

export default function SemanticSummaryView({
  raw,
  companyName,
  companyRole,
  onDeepDive,
  deepDivingIndex = null,
}: TSemanticSummaryViewProps) {
  const plan = useMemo(
    () => buildSummaryRenderPlan({ raw, companyName, companyRole }),
    [raw, companyName, companyRole]
  );
  const [showSecondary, setShowSecondary] = useState(false);
  const [heroExpanded, setHeroExpanded] = useState(false);

  if (!plan) return null;

  const primarySections = plan.sections.filter((s) => s.disclosure === "primary");
  const secondarySections = plan.sections.filter((s) => s.disclosure === "secondary");
  const heroNeedsClamp = (plan.hero.summary?.length ?? 0) > HERO_CLAMP_THRESHOLD;

  return (
    <div className="space-y-6">
      <AiAssistedDisclaimer context="summary" />

      {/* Hero — who is this, what do they do */}
      {(plan.hero.title ?? plan.hero.summary) && (
        <AppCard
          padding="lg"
          className="space-y-4 border-t-2 border-t-primary"
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Onboarding Summary</p>
            {roleLabel(companyRole) && (
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {roleLabel(companyRole)}
              </span>
            )}
          </div>
          {plan.hero.title && (
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">{plan.hero.title}</h2>
              {plan.hero.subtitle && <p className="mt-1 text-sm text-muted-foreground">{plan.hero.subtitle}</p>}
            </div>
          )}
          {plan.hero.summary && (
            <>
              {/* Constrained measure + relaxed leading: long-form prose is far
                  easier to read at ~75ch than stretched across the card. The
                  first paragraph reads as a lede. */}
              <div
                className={cn(
                  "markdown-content max-w-[75ch] text-[15px] leading-7 text-foreground [&_p:first-of-type]:text-base [&_p:last-child]:mb-0 [&_p]:mb-4",
                  heroNeedsClamp && !heroExpanded && "line-clamp-5"
                )}
              >
                <ReactMarkdown>{plan.hero.summary}</ReactMarkdown>
              </div>
              {heroNeedsClamp && (
                <button
                  onClick={() => setHeroExpanded((v) => !v)}
                  className="flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80"
                >
                  {heroExpanded ? (
                    <>
                      Show less <ChevronUp className="h-3 w-3" />
                    </>
                  ) : (
                    <>
                      Read full summary <ChevronDown className="h-3 w-3" />
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </AppCard>
      )}

      {/* KPI grid — auto-fits so one metric fills the row and many wrap evenly */}
      {plan.metrics.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
          {plan.metrics.map((metric) => {
            const MetricIcon = metricIcon(metric.label);
            return (
              <div
                key={metric.id}
                className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <MetricIcon className="h-3.5 w-3.5 text-primary" />
                  </span>
                  <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {metric.label}
                  </p>
                </div>
                <p className="mt-2.5 truncate text-2xl font-bold tabular-nums tracking-tight text-foreground">
                  {metric.display}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* AI insights — up to three per row so odd counts don't leave orphans */}
      {plan.insights.length > 0 && (
        <div>
          <div className="mb-3">
            <SectionHeader
              title="AI Insights"
              icon={Lightbulb}
            />
          </div>
          <div
            className={cn(
              "grid gap-2",
              plan.insights.length > 1 && "sm:grid-cols-2",
              plan.insights.length >= 3 && plan.insights.length % 2 === 1 && "lg:grid-cols-3"
            )}
          >
            {plan.insights.map((insight) => (
              <InsightCard
                key={insight.id}
                insight={insight}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recommendations — actionable, with deep-dive threads */}
      {plan.recommendations.length > 0 && (
        <div>
          <div className="mb-3">
            <SectionHeader
              title="Recommendations"
              icon={Sparkles}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-3">
            {plan.recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {idx + 1}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Recommendation
                  </span>
                </div>
                <p className="flex-1 text-sm leading-relaxed text-foreground">{rec}</p>
                {onDeepDive && (
                  <button
                    onClick={() => onDeepDive(rec, idx)}
                    disabled={deepDivingIndex !== null}
                    className="mt-3 flex items-center gap-1.5 self-start rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Search className="h-3 w-3" />
                    {deepDivingIndex === idx ? "Opening…" : "Deep Dive"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Primary sections */}
      {primarySections.map((section) => (
        <SectionCard
          key={section.id}
          section={section}
        />
      ))}

      {/* Secondary sections behind progressive disclosure */}
      {secondarySections.length > 0 && (
        <div>
          <button
            onClick={() => setShowSecondary((v) => !v)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/50 hover:text-foreground"
          >
            {showSecondary ? (
              <>
                Hide details <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                More details ({secondarySections.length}) <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
          {showSecondary && (
            <div className="mt-3 space-y-4">
              {secondarySections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
